/**
 * Verträge eines Kunden: Leistung, Turnus, Preis.
 *
 * Grundlage für Fälligkeiten und Angebote. Ein abgelaufener Vertrag wird
 * nicht gelöscht – er gehört zur Akte –, ist aber als abgelaufen
 * gekennzeichnet. Das rechnet der Server aus; die Oberfläche vergleicht
 * keine Datumsfelder.
 */

import { useState } from 'react'
import { PRUEFARTTEXT, type Pruefart } from '../api/typen'
import {
  useVertragAendern,
  useVertragAnlegen,
  useVertragLoeschen,
  useVertraege,
  type Vertrag,
} from '../api/kundenakte'
import { Fehlerhinweis, Ladeanzeige, datumDeutsch } from '../bausteine/Hinweise'
import { darfSchreiben, useKonto } from '../konto/KontoKontext'

const PRUEFARTEN = Object.keys(PRUEFARTTEXT) as Pruefart[]

function euro(betrag: string | null): string {
  if (!betrag) return ''
  return `${Number(betrag).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`
}

function turnusText(monate: number | null): string {
  if (!monate) return ''
  if (monate === 1) return 'monatlich'
  if (monate === 3) return 'vierteljährlich'
  if (monate === 6) return 'halbjährlich'
  if (monate === 12) return 'jährlich'
  return `alle ${monate} Monate`
}

export function Vertraege({ kundeId }: { kundeId: string }) {
  const { konto } = useKonto()
  const liste = useVertraege(kundeId)
  const anlegen = useVertragAnlegen(kundeId)
  const aendern = useVertragAendern(kundeId)
  const loeschen = useVertragLoeschen(kundeId)
  const [offen, setzeOffen] = useState(false)

  const schreiben = darfSchreiben(konto)

  if (liste.isLoading) return <Ladeanzeige />

  return (
    <div className="space-y-4">
      <Fehlerhinweis fehler={liste.error ?? anlegen.error ?? loeschen.error} />

      {schreiben &&
        (offen ? (
          <Formular
            speichern={async (felder) => {
              await anlegen.mutateAsync(felder)
              setzeOffen(false)
            }}
            abbrechen={() => setzeOffen(false)}
            laeuft={anlegen.isPending}
          />
        ) : (
          <button type="button" className="knopf-haupt" onClick={() => setzeOffen(true)}>
            Vertrag anlegen
          </button>
        ))}

      {liste.data?.length === 0 ? (
        <p className="text-slate-600">Noch kein Vertrag hinterlegt.</p>
      ) : (
        <ul className="space-y-2">
          {liste.data?.map((v) => (
            <li key={v.id} className={`karte ${v.laeuft ? '' : 'bg-slate-50'}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-semibold">{v.bezeichnung}</span>
                    {v.nummer && <span className="text-base text-slate-600">{v.nummer}</span>}
                    <span
                      className={`marke ${
                        v.laeuft
                          ? 'bg-stufe-ob-grund text-stufe-ob'
                          : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {v.laeuft ? 'läuft' : 'nicht in Kraft'}
                    </span>
                  </div>

                  <p className="mt-1 text-base text-slate-700">
                    {[
                      v.pruefart ? PRUEFARTTEXT[v.pruefart as Pruefart] : null,
                      turnusText(v.turnus_monate),
                      v.preis ? `${euro(v.preis)} ${v.je_platz ? 'je Platz' : 'pauschal'}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>

                  {(v.beginn || v.ende) && (
                    <p className="text-base text-slate-600">
                      {v.beginn ? `ab ${datumDeutsch(v.beginn)}` : ''}
                      {v.ende ? ` bis ${datumDeutsch(v.ende)}` : ''}
                      {v.kuendigungsfrist_monate
                        ? ` · Kündigungsfrist ${v.kuendigungsfrist_monate} Monate`
                        : ''}
                    </p>
                  )}

                  {v.bemerkung && (
                    <p className="mt-1 whitespace-pre-wrap text-base">{v.bemerkung}</p>
                  )}
                </div>

                {schreiben && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="knopf-still px-3"
                      onClick={() => aendern.mutate({ id: v.id, aktiv: !v.aktiv })}
                    >
                      {v.aktiv ? 'Stilllegen' : 'Aktivieren'}
                    </button>
                    <button
                      type="button"
                      className="knopf-still px-3"
                      onClick={() => loeschen.mutate(v.id)}
                    >
                      Löschen
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Formular({
  speichern,
  abbrechen,
  laeuft,
}: {
  speichern: (felder: Partial<Vertrag>) => Promise<void>
  abbrechen: () => void
  laeuft: boolean
}) {
  const [felder, setzeFelder] = useState({
    bezeichnung: '',
    nummer: '',
    pruefart: '',
    turnus_monate: '12',
    preis: '',
    je_platz: true,
    beginn: '',
    ende: '',
    kuendigungsfrist_monate: '',
    bemerkung: '',
  })

  function setze<K extends keyof typeof felder>(schluessel: K, wert: (typeof felder)[K]) {
    setzeFelder((alt) => ({ ...alt, [schluessel]: wert }))
  }

  return (
    <form
      className="karte space-y-3"
      onSubmit={(e) => {
        e.preventDefault()
        void speichern({
          bezeichnung: felder.bezeichnung,
          nummer: felder.nummer || null,
          pruefart: felder.pruefart || null,
          turnus_monate: felder.turnus_monate ? Number(felder.turnus_monate) : null,
          preis: felder.preis || null,
          je_platz: felder.je_platz,
          beginn: felder.beginn || null,
          ende: felder.ende || null,
          kuendigungsfrist_monate: felder.kuendigungsfrist_monate
            ? Number(felder.kuendigungsfrist_monate)
            : null,
          bemerkung: felder.bemerkung || null,
        })
      }}
    >
      <div>
        <label htmlFor="bezeichnung">Bezeichnung</label>
        <input
          id="bezeichnung"
          value={felder.bezeichnung}
          onChange={(e) => setze('bezeichnung', e.target.value)}
          placeholder="Rahmenvertrag Hauptuntersuchung"
          required
          maxLength={200}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="vnummer">Vertragsnummer</label>
          <input
            id="vnummer"
            value={felder.nummer}
            onChange={(e) => setze('nummer', e.target.value)}
            maxLength={60}
          />
        </div>
        <div>
          <label htmlFor="vpruefart">Leistung</label>
          <select
            id="vpruefart"
            value={felder.pruefart}
            onChange={(e) => setze('pruefart', e.target.value)}
          >
            <option value="">(keine Angabe)</option>
            {PRUEFARTEN.map((a) => (
              <option key={a} value={a}>
                {PRUEFARTTEXT[a]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="turnus">Turnus in Monaten</label>
          <input
            id="turnus"
            type="number"
            min={1}
            max={60}
            value={felder.turnus_monate}
            onChange={(e) => setze('turnus_monate', e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="preis">Preis in Euro</label>
          <input
            id="preis"
            type="number"
            step="0.01"
            min={0}
            value={felder.preis}
            onChange={(e) => setze('preis', e.target.value)}
          />
        </div>
      </div>

      <label className="flex items-center gap-3 font-normal">
        <input
          type="checkbox"
          checked={felder.je_platz}
          onChange={(e) => setze('je_platz', e.target.checked)}
          className="h-6 w-6 shrink-0"
          style={{ minHeight: 'auto' }}
        />
        <span>Preis gilt je Spielplatz (sonst pauschal je Durchgang)</span>
      </label>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label htmlFor="beginn">Beginn</label>
          <input
            id="beginn"
            type="date"
            value={felder.beginn}
            onChange={(e) => setze('beginn', e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="ende">Ende</label>
          <input
            id="ende"
            type="date"
            value={felder.ende}
            onChange={(e) => setze('ende', e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="frist">Kündigungsfrist (Monate)</label>
          <input
            id="frist"
            type="number"
            min={0}
            max={36}
            value={felder.kuendigungsfrist_monate}
            onChange={(e) => setze('kuendigungsfrist_monate', e.target.value)}
          />
        </div>
      </div>

      <div>
        <label htmlFor="vbemerkung">Bemerkung</label>
        <textarea
          id="vbemerkung"
          value={felder.bemerkung}
          onChange={(e) => setze('bemerkung', e.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <button type="submit" className="knopf-haupt" disabled={laeuft}>
          {laeuft ? 'Speichert …' : 'Anlegen'}
        </button>
        <button type="button" className="knopf-still" onClick={abbrechen}>
          Abbrechen
        </button>
      </div>
    </form>
  )
}
